import AddIcon from '@mui/icons-material/Add';
import FormHelperText from '@mui/material/FormHelperText';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FieldArray, useField } from 'formik';
import { useMemo } from 'react';
import AppWrappedInput from '../../AppWrappedInput';
import SchedulingSpecialtySearch from './SchedulingSpecialtySearch';

const SchedulingSpecialtyTable = ({
	formik,
	label,
	helperText,
	isRequired,
	visible = true,
	...props
}) => {
	const MAX_COUNT = 15;
	const [field, meta, helpers] = useField(props);

	const visibleRows = useMemo(() => {
		return Array.isArray(field.value) ? field.value : [];
	}, [field.value]);

	const headCells = [
		{
			id: 'isPrimary',
			numeric: false,
			hasHeaderIcon: false,
			label: 'Primary',
		},
		{
			id: 'schedulingSpecialtyName',
			numeric: false,
			hasHeaderIcon: false,
			label: 'Name',
		},
		{
			id: 'remove',
			numeric: false,
			hasHeaderIcon: false,
			label: '',
		},
	];

	return visible ? (
		<AppWrappedInput
			label={label}
			error={
				meta.touched
					? Array.isArray(meta.error)
						? 'Please fix the errors above'
						: meta.error
					: null
			}
			isRequired={isRequired}
		>
			<TableContainer>
				<Table
					sx={{ minWidth: 475, maxWidth: '100%' }}
					aria-labelledby='tableTitle'
					size={'small'}
					stickyHeader
				>
					<TableHead>
						<TableRow>
							{headCells.map(headCell => (
								<TableCell
									variant='head'
									key={headCell.id}
									align={headCell.numeric ? 'right' : 'left'}
									padding={
										headCell.hasHeaderIcon
											? 'none'
											: 'normal'
									}
								>
									<Typography variant='body1'>
										{headCell.label}
									</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<FieldArray name={props.name}>
						{({ remove, push, replace }) => (
							<TableBody>
								{visibleRows.length > 0 &&
									visibleRows.map((row, index) => (
										<TableRow
											hover
											role='checkbox'
											tabIndex={-1}
											key={index}
										>
											<TableCell padding='checkbox'>
												{row.isPrimary ? (
													<Tooltip
														title='Primary'
														placement='left'
													>
														<IconButton
															aria-label='Primary'
															sx={{
																color: 'gold',
															}}
														>
															<StarIcon />
														</IconButton>
													</Tooltip>
												) : (
													<Tooltip
														title='Set As Primary'
														placement='left'
													>
														<IconButton
															aria-label='Set As Primary'
															onClick={() => {
																helpers.setTouched(
																	true,
																);
																helpers.setValue(
																	visibleRows.map(
																		(
																			r,
																			i,
																		) => ({
																			...r,
																			isPrimary:
																				i ===
																				index,
																		}),
																	),
																);
															}}
															sx={{
																color: 'gold',
															}}
														>
															<StarOutlineIcon />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
											<TableCell
												component='th'
												scope='row'
											>
												<SchedulingSpecialtySearch
													value={row}
													allValues={visibleRows}
													setValue={v => {
														replace(index, {
															...visibleRows[
																index
															],
															specialtyId:
																v.specialtyId,
															specialtyName:
																v.specialtyName,
														});
													}}
													onBlur={() =>
														helpers.setTouched(true)
													}
													autoFocus={true}
													touched={meta.touched}
													error={
														Array.isArray(
															meta.error,
														) &&
														meta.error.length >=
															index
															? meta.error[index]
																	?.specialtyName
															: null
													}
												/>
											</TableCell>
											<TableCell padding='checkbox'>
												<Tooltip
													title='Click to remove this specialty from your selections'
													placement='left'
												>
													<IconButton
														aria-label='remove'
														onClick={() => {
															helpers.setTouched(
																true,
															);

															if (
																visibleRows.length >
																0
															) {
																const wasPrimary =
																	row.isPrimary;
																remove(index);

																if (
																	wasPrimary
																) {
																	replace(0, {
																		...visibleRows[0],
																		isPrimary: true,
																	});
																}
															}
														}}
													>
														<DeleteIcon />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									))}

								<TableRow
									style={{
										height: 33,
									}}
								>
									<TableCell colSpan={headCells?.length}>
										{visibleRows.length < MAX_COUNT && (
											<>
												<Button
													variant='contained'
													color='success'
													startIcon={<AddIcon />}
													onClick={() => {
														helpers.setTouched(
															true,
														);
														push({
															specialtyId: null,
															specialtyName: null,
															isPrimary: false,
														});
													}}
												>
													Add Specialty
												</Button>
												<FormHelperText>
													{helperText}
												</FormHelperText>
											</>
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						)}
					</FieldArray>
				</Table>
			</TableContainer>
		</AppWrappedInput>
	) : null;
};

export default SchedulingSpecialtyTable;
